import { domainEndpoint } from '@/api/axios';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { AttendanceForm, CalendarStoreInterface, MessageInterface } from '@/models/calendar.model';
import { CalendarFilter } from '@/models/filter.model';
import { PersistentStore } from '@/store/main';
import { Calendar } from '@fullcalendar/core';
import { domainStore } from '@/store/domain-store';
const options = {
  weekday: 'long',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

import useConverter from '@/composables/general/useConverter';
const CalendarRepository = RepositoryFactory.get('calendar');
const firstDayofMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 2).toISOString().slice(0, 10);
const lastDayofMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 32).toISOString().slice(0, 10);
const { UTCtoLocalTime } = useConverter();

class CalendarStore extends PersistentStore<CalendarStoreInterface> {
  protected data(): CalendarStoreInterface {
    return {
      calendar: null,
      isInitiated: false,
      isLoadingContent: false,
      isFilterMinimized: true,
      filter: {
        startDate: firstDayofMonth,
        endDate: lastDayofMonth,
        filterText: '',
      },
      calendarConfig: undefined,
      domainid: null,
      showEventDetail: false,
      singleEvent: {},
      attendanceMessage: null,
    };
  }

  showEventDetail(value: boolean) {
    this.state.showEventDetail = value;
  }
  isInitiated(value: boolean) {
    this.state.isInitiated = value;
  }
  isLoadingContent(value: boolean) {
    this.state.isLoadingContent = value;
  }
  isFilterMinimized(value: boolean) {
    this.state.isFilterMinimized = value;
  }
  toggleFilter() {
    this.state.isFilterMinimized = !this.state.isFilterMinimized;
  }

  setConfig() {
    CalendarRepository.getConfig().then((config: any) => {
      this.state.calendarConfig = config;
    });
  }

  setFilter(filter: CalendarFilter) {
    if (filter.startDate === undefined || filter.startDate === null) {
      filter.startDate = '';
    }
    if (filter.endDate === undefined || filter.endDate === null) {
      filter.endDate = '';
    }
    if (filter.filterText === undefined || filter.filterText === null) {
      filter.filterText = '';
    }
    this.state.filter = filter;
  }

  getSelfAttendance() {
    this.isLoadingContent(true);
    CalendarRepository.getSelfAttendance(this.state.singleEvent.id)
      .then((response: any) => {
        this.isLoadingContent(false);
        this.state.singleEvent.alreadyAttending = response.data.attendance.count_attendees;
        this.state.singleEvent.attendanceid = response.data.attendance.id;
        const messageKey = 'calendar.message.already-attending-event';
        this.setAttendanceMessage({ severity: 'info', content: messageKey });
        return messageKey;
      })
      .catch((error: any) => {
        // console.log('Keine Teilnahme gefunden \n', error);
        this.isLoadingContent(false);
      });
  }

  getSingleEvent(id: number) {
    this.isLoadingContent(true);
    if (this.state.singleEvent.id !== id) {
      this.state.attendanceMessage = null;
    }
    return CalendarRepository.fetchEventById(id)
      .then((event: any) => {
        this.state.singleEvent = event;
        //eslint-disable-next-line
        //@ts-ignore
        this.state.singleEvent.startDate = new Date(UTCtoLocalTime(event.start)).toLocaleDateString('de-DE', options);
        //eslint-disable-next-line
        //@ts-ignore
        this.state.singleEvent.endDate = new Date(UTCtoLocalTime(event.end)).toLocaleDateString('de-DE', options);
        this.state.singleEvent.links = event.links.web.href;
        //eslint-disable-next-line
        //@ts-ignore
        this.state.singleEvent.created = new Date(UTCtoLocalTime(event.created)).toLocaleDateString('de-DE', options);
        this.state.singleEvent.eventAttributes = this.getAttributes();
        return this.state.singleEvent;
      })
      .catch((error: any) => {
        this.state.singleEvent = {
          title: domainStore.translate('calendar.message.event-not-found'),
        };
        console.log(error);
        console.log('event not found');
      })
      .finally(() => {
        this.isLoadingContent(false);
      });
  }

  updateEvents(calendar: Calendar, params: any) {
    this.isLoadingContent(true);
    this.setFilter({ startDate: params.start, endDate: params.end, filterText: params.text });

    let base_url = process.env.VUE_APP_SERVER_BASE_URL;
    if (base_url == '/') {
      base_url = window.location.protocol + '//' + window.location.host;
    }

    if (!params.text || params.text === 'undefined' || params.text === null) {
      params.text = '';
    }
    if (!params.start && !params.end) {
      calendar.changeView('listYear');
    } else if (params.start && !params.end) {
      this.fullCalendarChangeViewToListWithRange(calendar, params.start, new Date().getFullYear() + 1 + '-01-01');
    } else if (!params.start && params.end) {
      this.fullCalendarChangeViewToListWithRange(calendar, new Date().getFullYear() + '-01-01', params.end);
    } else {
      this.fullCalendarChangeViewToListWithRange(calendar, params.start, params.end);
    }
    calendar.removeAllEventSources();
    calendar.removeAllEvents();
    calendar.addEventSource({
      url: base_url + '/api/v1/' + domainEndpoint() + '/calendar_entry/fullcalendar',
      method: 'GET',
      extraParams: {
        text: params.text,
        src: 'widgets',
      },
    });

    this.isLoadingContent(false);
  }

  fullCalendarChangeViewToListWithRange(calendar: any, startRange: any, endRange: any) {
    calendar.changeView('list');
    calendar.setOption('visibleRange', {
      start: startRange,
      end: endRange,
    });
  }

  // set headlines and classes for events
  getAttributes() {
    const eventAttributes: any = [];
    if (
      this.state.singleEvent &&
      this.state.singleEvent.attributes &&
      Object.entries(this.state.singleEvent.attributes).length !== 0
    ) {
      this.state.singleEvent.attributes[0].value_all.forEach((value: any, i: number) => {
        eventAttributes.push({
          attr: value,
          value: this.state.singleEvent.attributes[0].value_human_all[i],
          name: this.state.singleEvent.attributes[0].name,
        });
      });
    }
    return eventAttributes;
  }

  setAttendanceMessage(value: MessageInterface | null) {
    this.state.attendanceMessage = value;
  }

  verifyToken(eventid: number, attendanceid: number, token: string) {
    if (eventid && attendanceid && token) {
      this.isLoadingContent(true);
      this.setAttendanceMessage({ severity: 'info', content: 'calendar.message.waiting-on-submit' });

      return CalendarRepository.setVerifyToken(token, eventid, attendanceid)
        .then((response: any) => {
          if (response && response.data.success) {
            this.setAttendanceMessage({ severity: 'success', content: 'calendar.message.verification-success' });
          } else {
            this.setAttendanceMessage({ severity: 'error', content: 'calendar.message.verification-error' });
          }
        })
        .catch((error: any) => {
          console.log(error);
          this.setAttendanceMessage({
            severity: 'error',
            content: 'general.message.generic-error',
          });
        })
        .finally(() => {
          this.isLoadingContent(false);
        });
    }
  }

  private validateForm(form: AttendanceForm, isUserLoggedIn: boolean) {
    if (!isUserLoggedIn) {
      if (this.state.calendarConfig.name_required && !form.name) {
        console.log('nameError');
        return false;
      }
      if (!form.email) {
        console.log('emailError');
        return false;
      }
    }
    if (this.state.calendarConfig.count_attendees_required && form.countAttendees === 'Personen') {
      console.log('attendeesError');
      return false;
    }
    if (this.state.calendarConfig.comment_required && !form.comment) {
      console.log('commentError');
      return false;
    }
    return true;
  }

  addAttendance(form: any, userIsLoggedIn: boolean) {
    return new Promise((resolve, reject) => {
      if (this.validateForm(form, userIsLoggedIn)) {
        const attendeeObj: any = {
          name: form.name,
          email: form.email,
          count_attendees: form.countAttendees,
          comment: form.comment,
        };
        this.setAttendanceMessage({ severity: 'info', content: 'calendar.message.waiting-on-submit' });
        this.isLoadingContent(true);

        // attribute values
        Object.keys(form).forEach(formKey => {
          if (formKey.startsWith('attribute-')) {
            const value = form[formKey];
            attendeeObj[formKey] = typeof value == 'boolean' ? (value ? 1 : 0) : value;
          }
        });

        // console.log(attendeeObj);
        CalendarRepository.setSelfAttendance(this.state.singleEvent.id!, attendeeObj)
          .then((response: any) => {
            this.setAttendanceMessage({
              severity: 'success',
              content:
                response.verified === 1
                  ? 'calendar.message.attendance-success'
                  : 'calendar.message.attendance-success-verification-required',
            });
            this.isLoadingContent(false);
            resolve(response);
          })
          .catch((error: any) => {
            console.error(error.response);
            if (error.response.status === 400) {
              this.setAttendanceMessage({
                severity: 'error',
                content:
                  error.response.data.message === 'user is already registered'
                    ? 'calendar.message.already-attending-event'
                    : 's.generic-error',
              });
            } else {
              this.setAttendanceMessage({
                severity: 'error',
                content: 'general.message.generic-error',
              });
            }
            this.isLoadingContent(false);
            reject(error);
          });
      } else return console.log('Form validation failed');
    });
  }
}

export const calendarStore: CalendarStore = new CalendarStore('CALENDAR_STORE');
